import React, { useState, useMemo, useCallback, useRef, useEffect } from 'react';
import { Plus, Minus, Trash2, AlertCircle, CheckCircle2 } from 'lucide-react';
import InlineScanner from '../../scanner/components/InlineScanner';
import { canonicalUpc } from '../../scanner/components/scannerUtils';
import { ScannerMode } from '../../../lib/contracts';
import { useCoreContextOptional } from '../../../lib/coreContext';
import { bottleReturnsApi } from '../api/bottleReturns.api';
import type { UpcEligibility } from '../types/bottleReturns.types';
import { formatCurrency } from '@/lib/formatters';

interface ReturnUpcEntry {
  upc: string;
  quantity: number;
}

interface CustomerReturnScannerProps {
  onComplete: (upcs: ReturnUpcEntry[], estimatedCredit: number) => void;
  onChange?: (totalContainers: number, estimatedCredit: number) => void;
  onUncountedBagChange?: (attached: boolean) => void;
  onStatePersist?: (payload: {
    upcs: ReturnUpcEntry[];
    eligibilityCache: Record<string, UpcEligibility>;
    reason: 'hydrate' | 'add' | 'increment' | 'decrement' | 'remove' | 'clear';
  }) => void | Promise<void>;
  initialUpcs?: ReturnUpcEntry[];
  initialEligibilityCache?: Record<string, UpcEligibility>;
  isAuthenticated?: boolean;
  className?: string;
}

const LS_KEY_UPCS = 'ninpo_return_upcs_v1';
const LS_KEY_ELIGIBILITY = 'ninpo_upc_eligibility_v1';
export const LS_KEY_UNCOUNTED_BAG = 'ninpo_uncounted_bag_v1';
export const UNCOUNTED_BAG_DRIVER_NOTE =
  'UNCOUNTED BAG: Customer attached a bottle-return bag for the driver to count at delivery.';
const ELIGIBILITY_TTL_MS = 60 * 60 * 1000; // 1 hour
const MI_DEPOSIT_VALUE = 0.1;
const DEFAULT_HANDLING_FEE = 0.02;
const DEFAULT_GLASS_FEE = 0.02;

function money(n: number) {
  return formatCurrency(Number.isFinite(n) ? n : 0);
}

export function readUncountedBagAttached(): boolean {
  try {
    return localStorage.getItem(LS_KEY_UNCOUNTED_BAG) === '1';
  } catch {
    return false;
  }
}

export function writeUncountedBagAttached(attached: boolean): void {
  try {
    if (attached) localStorage.setItem(LS_KEY_UNCOUNTED_BAG, '1');
    else localStorage.removeItem(LS_KEY_UNCOUNTED_BAG);
  } catch {
    // private mode — ignore
  }
}

const DEFAULT_INITIAL_UPCS: ReturnUpcEntry[] = [];
const DEFAULT_INITIAL_ELIGIBILITY: Record<string, UpcEligibility> = {};

const CustomerReturnScanner: React.FC<CustomerReturnScannerProps> = ({
  onComplete,
  onChange,
  onUncountedBagChange,
  onStatePersist,
  initialUpcs = DEFAULT_INITIAL_UPCS,
  initialEligibilityCache = DEFAULT_INITIAL_ELIGIBILITY,
  isAuthenticated = false,
  className = ''
}) => {
  const core = useCoreContextOptional();
  const [returnUpcs, setReturnUpcs] = useState<ReturnUpcEntry[]>([]);
  const [eligibilityCache, setEligibilityCache] = useState<Record<string, UpcEligibility>>({});
  const [scanError, setScanError] = useState<string | null>(null);
  const [scanStatus, setScanStatus] = useState<string | null>(null);
  const [isChecking, setIsChecking] = useState(false);
  const [manualUpc, setManualUpc] = useState('');
  const [uncountedBagAttached, setUncountedBagAttached] = useState(() => readUncountedBagAttached());

  const lastScanAtRef = useRef(0);
  const eligibilityCacheRef = useRef<Record<string, UpcEligibility>>({});
  const returnUpcsRef = useRef<ReturnUpcEntry[]>([]);

  useEffect(() => {
    returnUpcsRef.current = returnUpcs;
  }, [returnUpcs]);

  const mergeUpcs = useCallback((primary: ReturnUpcEntry[], fallback: ReturnUpcEntry[]) => {
    const merged = new Map<string, ReturnUpcEntry>();
    for (const entry of primary) {
      if (!entry?.upc || entry.quantity <= 0) continue;
      merged.set(entry.upc, { upc: entry.upc, quantity: entry.quantity });
    }
    for (const entry of fallback) {
      if (!entry?.upc || entry.quantity <= 0 || merged.has(entry.upc)) continue;
      merged.set(entry.upc, { upc: entry.upc, quantity: entry.quantity });
    }
    return Array.from(merged.values());
  }, []);

  const persistState = useCallback((upcs: ReturnUpcEntry[], reason: 'hydrate' | 'add' | 'increment' | 'decrement' | 'remove' | 'clear') => {
    onStatePersist?.({
      upcs,
      eligibilityCache: eligibilityCacheRef.current,
      reason
    });
  }, [onStatePersist]);

  // Hydrate scanner state: server/parent data first, then local fallback.
  useEffect(() => {
    let localUpcs: ReturnUpcEntry[] = [];
    let localEligibility: Record<string, UpcEligibility> = {};
    try {
      const stored = localStorage.getItem(LS_KEY_UPCS);
      if (stored) {
        const parsed = JSON.parse(stored);
        if (Array.isArray(parsed)) {
          localUpcs = parsed
            .map((entry: unknown) => {
              if (!entry || typeof entry !== 'object') return null;
              const candidate = entry as { upc?: unknown; quantity?: unknown };
              const upc = String(candidate.upc || '').replace(/\s+/g, '').trim();
              const quantity = Number(candidate.quantity);
              if (!/^\d{8,14}$/.test(upc) || !Number.isFinite(quantity) || quantity <= 0) return null;
              return { upc, quantity: Math.floor(quantity) };
            })
            .filter((entry): entry is ReturnUpcEntry => Boolean(entry));
        }
      }
    } catch {}
    try {
      const storedCache = localStorage.getItem(LS_KEY_ELIGIBILITY);
      if (storedCache) {
        const parsed = JSON.parse(storedCache);
        if (parsed && typeof parsed === 'object') {
          localEligibility = Object.entries(parsed).reduce<Record<string, UpcEligibility>>((acc, [rawUpc, rawEntry]) => {
            const upc = String(rawUpc || '').replace(/\s+/g, '').trim();
            if (!/^\d{8,14}$/.test(upc) || !rawEntry || typeof rawEntry !== 'object') return acc;
            const entry = rawEntry as Partial<UpcEligibility>;
            acc[upc] = {
              isEligible: entry.isEligible === true,
              name: typeof entry.name === 'string' ? entry.name : undefined,
              containerType: typeof entry.containerType === 'string' ? entry.containerType : undefined,
              isGlass: entry.isGlass === true,
              sizeOz: typeof entry.sizeOz === 'number' ? entry.sizeOz : undefined,
              reason: typeof entry.reason === 'string' ? entry.reason : undefined,
              reasonCode: typeof entry.reasonCode === 'string' ? entry.reasonCode : undefined,
              checkedAt: typeof entry.checkedAt === 'string' ? entry.checkedAt : new Date(0).toISOString()
            };
            return acc;
          }, {});
        }
      }
    } catch {}

    const baseUpcs = initialUpcs.length > 0 ? initialUpcs : localUpcs;
    const mergedUpcs = isAuthenticated ? mergeUpcs(baseUpcs, localUpcs) : baseUpcs;
    const mergedEligibility = {
      ...localEligibility,
      ...initialEligibilityCache
    };
    eligibilityCacheRef.current = mergedEligibility;
    setEligibilityCache(mergedEligibility);
    setReturnUpcs(mergedUpcs);
    persistState(mergedUpcs, 'hydrate');
  }, [initialUpcs, initialEligibilityCache, isAuthenticated, mergeUpcs, persistState]);

  useEffect(() => {
    if (returnUpcs.length > 0) {
      try {
        localStorage.setItem(LS_KEY_UPCS, JSON.stringify(returnUpcs));
      } catch {}
    } else {
      try {
        localStorage.removeItem(LS_KEY_UPCS);
      } catch {}
    }
  }, [returnUpcs]);

  useEffect(() => {
    if (Object.keys(eligibilityCache).length > 0) {
      try {
        localStorage.setItem(LS_KEY_ELIGIBILITY, JSON.stringify(eligibilityCache));
      } catch {}
    }
  }, [eligibilityCache]);

  const updateEligibilityCache = useCallback((upc: string, data: UpcEligibility) => {
    const entry: UpcEligibility = {
      ...data,
      checkedAt: typeof data.checkedAt === 'string' ? data.checkedAt : new Date().toISOString()
    };
    eligibilityCacheRef.current[upc] = entry;
    setEligibilityCache(prev => ({ ...prev, [upc]: entry }));
  }, []);

  const isEligibilityCacheFresh = (checkedAt?: string) => {
    if (!checkedAt) return false;
    const parsed = Date.parse(checkedAt);
    if (!Number.isFinite(parsed)) return false;
    return Date.now() - parsed < ELIGIBILITY_TTL_MS;
  };

  const addOrIncrementUpc = useCallback((upc: string, label?: string) => {
    let newQty = 1;
    setReturnUpcs(prev => {
      const existing = prev.find(e => e.upc === upc);
      newQty = existing ? existing.quantity + 1 : 1;
      const next = !existing
        ? [{ upc, quantity: 1 }, ...prev]
        : prev.map(e => (e.upc === upc ? { ...e, quantity: e.quantity + 1 } : e));
      persistState(next, existing ? 'increment' : 'add');
      return next;
    });
    setScanError(null);
    const existingEntry = returnUpcsRef.current.find(e => e.upc === upc);
    const resolvedQty = existingEntry ? existingEntry.quantity + 1 : newQty;
    const qtySuffix = resolvedQty > 1 ? ` (${resolvedQty}x)` : '';
    const status = label ? `Added ${label}${qtySuffix}` : `Added ${upc}${qtySuffix}`;
    setScanStatus(status);
    core?.addToast?.(status, 'success');
    if (typeof navigator !== 'undefined' && 'vibrate' in navigator) {
      try {
        navigator.vibrate(60);
      } catch {}
    }
  }, [core, persistState]);

  const rejectScan = useCallback((message: string) => {
    setScanError(message);
    setScanStatus(null);
    core?.addToast?.(message, 'error');
    if (typeof navigator !== 'undefined' && 'vibrate' in navigator) {
      try {
        navigator.vibrate([40, 40, 40]);
      } catch {}
    }
  }, [core]);

  const handleScan = useCallback(async (upcRaw: string) => {
    const upc = canonicalUpc(String(upcRaw || '').replace(/\s+/g, '').trim());
    if (!upc) return;

    const now = Date.now();
    if (now - lastScanAtRef.current < 450) return;
    lastScanAtRef.current = now;

    if (!/^\d{8,14}$/.test(upc)) {
      rejectScan('Invalid UPC format (8-14 digits required)');
      return;
    }

    const cached = eligibilityCacheRef.current[upc];
    if (cached && isEligibilityCacheFresh(cached.checkedAt)) {
      if (!cached.isEligible) {
        rejectScan(cached.reason || "This container isn't eligible for return");
        return;
      }
      addOrIncrementUpc(upc, cached.name);
      return;
    }

    setIsChecking(true);
    setScanStatus('Verifying Michigan deposit with backend…');
    try {
      const eligibility = await bottleReturnsApi.checkUpcEligibility(upc);
      updateEligibilityCache(upc, eligibility);

      if (!eligibility.isEligible) {
        rejectScan(eligibility.reason || "This container isn't eligible for return");
        return;
      }

      addOrIncrementUpc(upc, eligibility.name);
    } catch {
      rejectScan('Unable to validate UPC with backend. Please try again.');
    } finally {
      setIsChecking(false);
    }
  }, [addOrIncrementUpc, rejectScan, updateEligibilityCache]);

  const handleManualAdd = useCallback(async (e?: React.FormEvent) => {
    e?.preventDefault();
    const upc = canonicalUpc(manualUpc.trim());
    if (!upc) return;
    await handleScan(upc);
    setManualUpc('');
  }, [manualUpc, handleScan]);

  const incrementUpc = useCallback((upc: string) => {
    setReturnUpcs(prev => {
      const next = prev.map(e => (e.upc === upc ? { ...e, quantity: e.quantity + 1 } : e));
      persistState(next, 'increment');
      return next;
    });
  }, [persistState]);

  const decrementUpc = useCallback((upc: string) => {
    setReturnUpcs(prev => {
      const next = prev
        .map(e => (e.upc === upc ? { ...e, quantity: Math.max(0, e.quantity - 1) } : e))
        .filter(e => e.quantity > 0);
      persistState(next, 'decrement');
      return next;
    });
  }, [persistState]);

  const removeUpc = useCallback((upc: string) => {
    setReturnUpcs(prev => {
      const next = prev.filter(e => e.upc !== upc);
      persistState(next, 'remove');
      return next;
    });
  }, [persistState]);

  const clearAll = useCallback(() => {
    setReturnUpcs([]);
    setScanError(null);
    setScanStatus(null);
    persistState([], 'clear');
  }, [persistState]);

  const {
    totalCount,
    cansCount,
    bottlesCount,
    glassCount,
    grossCredit,
    handlingFeeTotal,
    glassFeeTotal,
    netCredit
  } = useMemo(() => {
    let cans = 0;
    let bottles = 0;
    let glass = 0;
    let total = 0;
    let gross = 0;
    let handling = 0;
    let glassFee = 0;

    for (const entry of returnUpcs) {
      const info = eligibilityCache[entry.upc];
      const type = (info?.containerType || 'aluminum').toLowerCase();
      const isGlass = type === 'glass' || info?.isGlass;
      total += entry.quantity;
      gross += MI_DEPOSIT_VALUE * entry.quantity;
      handling += DEFAULT_HANDLING_FEE * entry.quantity;

      if (isGlass) {
        glass += entry.quantity;
        glassFee += DEFAULT_GLASS_FEE * entry.quantity;
      } else if (type === 'plastic' || type === 'bottle') {
        bottles += entry.quantity;
      } else {
        cans += entry.quantity;
      }
    }

    return {
      totalCount: total,
      cansCount: cans,
      bottlesCount: bottles,
      glassCount: glass,
      grossCredit: gross,
      handlingFeeTotal: handling,
      glassFeeTotal: glassFee,
      netCredit: Math.max(0, gross - handling - glassFee)
    };
  }, [returnUpcs, eligibilityCache]);

  useEffect(() => {
    onChange?.(totalCount, netCredit);
  }, [totalCount, netCredit, onChange]);

  const attachUncountedBag = useCallback(() => {
    writeUncountedBagAttached(true);
    setUncountedBagAttached(true);
    onUncountedBagChange?.(true);
    setScanError(null);
    setScanStatus('Uncounted bag attached — driver will count at delivery');
    core?.addToast?.('Uncounted bag attached. Add snacks and checkout — your driver will count the bag.', 'success');
    onComplete(returnUpcs, netCredit);
  }, [core, netCredit, onComplete, onUncountedBagChange, returnUpcs]);

  const detachUncountedBag = useCallback(() => {
    writeUncountedBagAttached(false);
    setUncountedBagAttached(false);
    onUncountedBagChange?.(false);
    setScanStatus(null);
  }, [onUncountedBagChange]);

  return (
    <div className={`space-y-4 ${className}`}>
      <InlineScanner
        mode={ScannerMode.CUSTOMER_RETURN_SCAN}
        onScan={handleScan}
        title="Bottle Return Scanner"
        subtitle="Point at the barcode — eligible returns apply as checkout credit"
        beepEnabled={true}
        cooldownMs={450}
        manualStart={true}
        showManualFallback={false}
        className="min-h-[280px] sm:min-h-[320px]"
      />

      {(scanError || scanStatus || isChecking) && (
        <div
          className={`rounded-2xl border px-4 py-3 flex items-start gap-3 ${
            scanError
              ? 'bg-ninpo-red/10 border-ninpo-red/20'
              : 'bg-ninpo-lime/10 border-ninpo-lime/20'
          }`}
          role="status"
          aria-live="polite"
        >
          {scanError ? (
            <AlertCircle className="w-5 h-5 text-ninpo-red shrink-0 mt-0.5" />
          ) : (
            <CheckCircle2 className="w-5 h-5 text-ninpo-lime shrink-0 mt-0.5" />
          )}
          <p
            className={`text-[11px] font-bold uppercase tracking-widest ${
              scanError ? 'text-ninpo-red' : 'text-ninpo-lime'
            }`}
          >
            {scanError || (isChecking ? 'Checking Michigan registry…' : scanStatus)}
          </p>
        </div>
      )}

      <form onSubmit={handleManualAdd} className="flex gap-2">
        <input
          type="text"
          inputMode="numeric"
          pattern="[0-9]*"
          placeholder="Or enter barcode (8–14 digits)"
          value={manualUpc}
          onChange={(e) => setManualUpc(e.target.value.replace(/\D/g, ''))}
          className="flex-1 px-4 py-2.5 bg-black/40 border border-white/10 rounded-xl text-xs text-white placeholder-slate-500 focus:outline-none focus:border-ninpo-lime/60 font-mono"
        />
        <button
          type="submit"
          disabled={!manualUpc.trim() || isChecking}
          className="px-4 py-2.5 bg-ninpo-lime/20 hover:bg-ninpo-lime/30 border border-ninpo-lime/30 text-ninpo-lime disabled:opacity-40 disabled:border-white/10 disabled:text-slate-500 rounded-xl text-xs font-black uppercase tracking-wider transition"
        >
          Add
        </button>
      </form>

      {returnUpcs.length > 0 ? (
        <div className="space-y-3">
          <div className="flex items-center justify-between px-1">
            <div>
              <p className="text-[10px] text-slate-500 font-black uppercase tracking-widest">
                Return containers · {totalCount}
              </p>
              <p className="text-xs text-ninpo-lime font-bold tabular-nums">
                Est. credit {money(netCredit)} (finalized at checkout)
              </p>
            </div>
            <button
              type="button"
              onClick={clearAll}
              className="text-[10px] text-ninpo-red font-black uppercase tracking-widest hover:underline"
            >
              Clear
            </button>
          </div>

          {/* Container Breakdown Chips */}
          <div className="flex flex-wrap items-center gap-1.5 px-1">
            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-lg bg-emerald-500/10 border border-emerald-500/20 text-emerald-400 text-[10px] font-bold">
              🥫 {cansCount} Cans
            </span>
            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-lg bg-cyan-500/10 border border-cyan-500/20 text-cyan-400 text-[10px] font-bold">
              🍾 {bottlesCount} Plastic
            </span>
            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-lg bg-amber-500/10 border border-amber-500/20 text-amber-400 text-[10px] font-bold">
              🍷 {glassCount} Glass
            </span>
          </div>

          {/* Michigan Deposit Fee Notice */}
          <div className="rounded-xl bg-white/[0.03] border border-white/5 px-3 py-2 text-[10px] text-slate-400 leading-relaxed flex items-center justify-between">
            <span>MI 10¢ Deposit (-2¢ handling fee/ea{glassFeeTotal > 0 ? ', -2¢ glass fee/ea' : ''})</span>
            <span className="font-mono font-bold text-slate-300 tabular-nums">Net: {money(netCredit)}</span>
          </div>

          <div className="space-y-2 max-h-[240px] overflow-y-auto pr-1">
            {returnUpcs.map(entry => {
              const info = eligibilityCache[entry.upc];
              const rawType = (info?.containerType || 'aluminum').toLowerCase();
              const isGlass = rawType === 'glass' || info?.isGlass;
              const isPlastic = !isGlass && (rawType === 'plastic' || rawType === 'bottle');
              const containerLabel = isGlass ? '🍷 Glass' : isPlastic ? '🍾 Plastic' : '🥫 Can';
              const badgeClass = isGlass
                ? 'bg-amber-500/10 border-amber-500/20 text-amber-400'
                : isPlastic
                ? 'bg-cyan-500/10 border-cyan-500/20 text-cyan-400'
                : 'bg-emerald-500/10 border-emerald-500/20 text-emerald-400';

              return (
                <div
                  key={entry.upc}
                  className={`bg-black/30 border rounded-2xl px-4 py-3 space-y-2 ${
                    isGlass ? 'border-yellow-500/20' : 'border-white/10'
                  }`}
                >
                  <div className="flex items-start justify-between gap-3">
                    <div className="flex-1 min-w-0">
                      <p className="text-[11px] text-white font-bold tracking-wider truncate">
                        {info?.name || entry.upc}
                      </p>
                      <div className="flex items-center gap-2 mt-1">
                        <span className="text-[9px] text-slate-500 font-bold uppercase tracking-widest font-mono">
                          {entry.upc}
                        </span>
                        <span className={`inline-flex items-center px-1.5 py-0.5 rounded-md border text-[9px] font-bold uppercase tracking-wider ${badgeClass}`}>
                          {containerLabel}
                        </span>
                      </div>
                    </div>

                    <div className="flex items-center gap-2 shrink-0">
                      <button
                        type="button"
                        onClick={() => decrementUpc(entry.upc)}
                        className="p-2 rounded-xl bg-white/5 border border-white/10 text-white hover:bg-white/10 transition"
                        aria-label="Decrease quantity"
                      >
                        <Minus className="w-3 h-3" />
                      </button>

                      <div className="w-10 text-center">
                        <p className="text-white font-black text-sm">{entry.quantity}</p>
                      </div>

                      <button
                        type="button"
                        onClick={() => incrementUpc(entry.upc)}
                        className="p-2 rounded-xl bg-white/5 border border-white/10 text-white hover:bg-white/10 transition"
                        aria-label="Increase quantity"
                      >
                        <Plus className="w-3 h-3" />
                      </button>

                      <button
                        type="button"
                        onClick={() => removeUpc(entry.upc)}
                        className="p-2 rounded-xl bg-ninpo-red/10 border border-ninpo-red/20 text-ninpo-red hover:bg-ninpo-red/20 transition ml-1"
                        aria-label="Remove return"
                      >
                        <Trash2 className="w-3 h-3" />
                      </button>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>

          <button
            type="button"
            onClick={() => onComplete(returnUpcs, netCredit)}
            className="w-full py-3.5 bg-white/10 border border-white/15 text-white rounded-2xl text-[11px] font-black uppercase tracking-widest hover:bg-white/15 transition"
          >
            Done scanning — credit stays on this order
          </button>
        </div>
      ) : (
        <div className="bg-white/5 border border-white/10 rounded-2xl p-4 text-center space-y-3">
          <p className="text-[11px] text-slate-400 font-bold uppercase tracking-widest">
            Returns are not cart products — they apply as deposit credit at checkout
          </p>
          {uncountedBagAttached ? (
            <>
              <p className="text-[10px] text-ninpo-lime font-bold leading-relaxed">
                Uncounted bag attached. Hand it to your driver at delivery — they count in-app and credit your wallet.
              </p>
              <div className="rounded-xl border border-amber-500/30 bg-amber-500/10 p-3 text-left space-y-1">
                <div className="flex items-center gap-1.5 text-amber-300 font-black text-[11px] uppercase tracking-wider">
                  <AlertCircle className="w-4 h-4 text-amber-400 shrink-0" />
                  <span>Uncounted Bag Fee Notice (!)</span>
                </div>
                <p className="text-[10px] text-amber-100/90 leading-relaxed">
                  Your driver will physically count and verify your bag at delivery. A standard counting fee of <strong>2¢/container</strong> will be deducted from your 10¢ deposit refund (you receive <strong>8¢/container net</strong> credited to your order or wallet). 1¢/container goes directly to your driver for counting time.
                </p>
              </div>
              <button
                type="button"
                onClick={detachUncountedBag}
                className="w-full py-3 rounded-xl bg-white/10 border border-white/15 text-white font-black text-[10px] uppercase tracking-widest hover:bg-white/15 transition"
              >
                Remove uncounted bag
              </button>
              <button
                type="button"
                onClick={() => onComplete([], 0)}
                className="w-full py-3 rounded-xl bg-ninpo-lime/20 border border-ninpo-lime/30 text-ninpo-lime font-black text-[10px] uppercase tracking-widest hover:bg-ninpo-lime/30 transition"
              >
                Done — keep bag on this order
              </button>
            </>
          ) : (
            <>
              <p className="text-[10px] text-slate-500 leading-relaxed">
                Have an uncounted bag? Attach it here, then checkout with snacks. Your driver counts it at delivery and credits your wallet.
              </p>
              <div className="rounded-xl border border-amber-500/30 bg-amber-500/10 p-3 text-left space-y-1">
                <div className="flex items-center gap-1.5 text-amber-300 font-black text-[11px] uppercase tracking-wider">
                  <AlertCircle className="w-4 h-4 text-amber-400 shrink-0" />
                  <span>Counting Fee Warning (!)</span>
                </div>
                <p className="text-[10px] text-amber-100/90 leading-relaxed">
                  Letting Ninpo/driver count your bag incurs a <strong>2¢/container</strong> physical handling fee (you receive <strong>8¢ net credit</strong> per container instead of 10¢). Pre-scanning containers yourself above is 100% free with <strong>$0 fee</strong>.
                </p>
              </div>
              <button
                type="button"
                onClick={attachUncountedBag}
                className="w-full py-3 rounded-xl bg-ninpo-lime/20 border border-ninpo-lime/30 text-ninpo-lime font-black text-[10px] uppercase tracking-widest hover:bg-ninpo-lime/30 transition"
              >
                Attach uncounted bag (driver counts)
              </button>
            </>
          )}
        </div>
      )}
    </div>
  );
};

export default CustomerReturnScanner;
